import React from "react";
import { ShieldCheck, Clock, Lock, UserCheck, Globe, Bell, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className='max-w-4xl mx-auto px-6 py-12'>
      {/* Page Header */}
      <div className='mb-10 text-center'>
        <div className='inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-4'>
          <ShieldCheck className='h-8 w-8 text-blue-600' />
        </div>
        <h1 className='text-3xl font-bold text-gray-900 mb-2'>Privacy Policy</h1>
        <p className='text-lg text-gray-600 max-w-2xl mx-auto'>
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information
          when you visit our website or make a purchase.
        </p>
      </div>

      {/* Policy Last Updated */}
      <div className='flex items-center justify-center mb-10 text-sm text-gray-500'>
        <Clock className='h-4 w-4 mr-1.5' />
        Last updated: December 2025
      </div>

      {/* Introduction */}
      <Card className='p-6 border-0 shadow-sm mb-8'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>Introduction</h2>
        <p className='text-gray-700 mb-4'>
          Stedshop LTD ("Stedshop," "We," "Us," or "Our") has created this Privacy Policy to explain
          how we collect, use, disclose, and protect your Personal Data (also referred to as
          personal information or personally identifiable information), including any other
          information you provide to us.
        </p>
        <p className='text-gray-700 mb-4'>
          By providing your Personal Data on our website (https://stedshop.com.ng) or by clicking
          "Accept" (e.g., during registration or checkout), you consent to the collection,
          processing, and use of your Personal Data as described in this Privacy Policy and in
          accordance with applicable Nigerian law. You may withdraw your consent at any time, and we
          will discontinue processing your data unless we have another lawful basis (e.g., legal
          obligation or contract performance).
        </p>
        <p className='text-gray-700 mb-4'>
          Stedshop LTD is committed to safeguarding your privacy and ensuring transparency in how we
          handle your information. We operate as a reseller of finished clothing items—including
          branded apparel (e.g., Nike) and products sourced from local vendors—and do not
          manufacture or customize garments.
        </p>
        <p className='text-gray-700'>
          We may update this Privacy Policy from time to time. If we make material changes, we will
          revise the "Last Updated" date at the top and, where appropriate, provide additional
          notice (e.g., via email or a banner on our website). We encourage you to review this
          policy regularly.
        </p>
      </Card>

      {/* 1. What This Privacy Policy Covers */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='coverage'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>1. What This Privacy Policy Covers</h2>
        <p className='text-gray-700 mb-3'>This policy explains:</p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>How we collect information from you</li>
          <li>How we use and share your Personal Data</li>
          <li>How we protect your information</li>
          <li>Your rights as a data subject under the Nigeria Data Protection Act (NDPA) 2023</li>
          <li>General data handling practices in compliance with NDPC guidelines</li>
        </ul>
        <p className='text-gray-700'>
          This policy does not apply to third-party websites, platforms, or services not under our
          control (e.g., payment gateways, social media links).
        </p>
      </Card>

      {/* 2. Collection of Personal Data */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='collection'>
        <div className='flex items-start mb-4'>
          <UserCheck className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>2. Collection of Personal Data</h2>
          </div>
        </div>
        <p className='text-gray-700 mb-4'>
          We may collect and process the following categories of Personal Data:
        </p>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>
          a. Information You Provide Directly
        </h3>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>
            <strong>Account/Registration Data:</strong> Name, email address, phone number,
            shipping/billing address
          </li>
          <li>
            <strong>Order Information:</strong> Product details, payment method (note: full card
            details are processed by secure third-party payment processors), delivery preferences
          </li>
          <li>
            <strong>Customer Support:</strong> Records of calls, emails, or chat messages when you
            contact us
          </li>
          <li>
            <strong>Returns & Refunds:</strong> Photos or descriptions of returned items (if
            requested for verification)
          </li>
        </ul>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>
          b. Automatically Collected Data
        </h3>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>
            <strong>Device & Usage Information:</strong> IP address, browser type, operating system,
            pages visited, time spent on site, click patterns
          </li>
          <li>
            <strong>Location Data:</strong> Approximate location (derived from IP) to optimize
            delivery and service availability within Nigeria
          </li>
        </ul>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>
          c. Data from Third Parties
        </h3>
        <p className='text-gray-700'>
          If you log in or engage with us via social media (e.g., Instagram, Facebook), we may
          receive limited profile information (e.g., name, profile picture) with your permission. We
          will notify you and explain the purpose at the time of collection.
        </p>
      </Card>

      {/* 3. Use of Personal Data */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='use'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>3. Use of Personal Data</h2>
        <p className='text-gray-700 mb-3'>
          We use your Personal Data for the following lawful purposes:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>To process and deliver your orders (including returns and refunds)</li>
          <li>To communicate with you about your account, orders, or customer support inquiries</li>
          <li>To detect, prevent, or investigate fraud, abuse, or illegal activity</li>
          <li>
            To comply with legal and regulatory obligations (e.g., tax, anti-fraud, NDPC reporting)
          </li>
          <li>To improve our website, products, and customer experience (e.g., via analytics)</li>
          <li>To send service-related messages (e.g., delivery updates via SMS or email)</li>
          <li>
            To offer personalized recommendations based on your browsing/purchase history (you may
            opt out)
          </li>
        </ul>
        <p className='text-gray-700 font-semibold'>
          We do not sell your Personal Data to third parties for marketing purposes without your
          explicit consent.
        </p>
      </Card>

      {/* 4. Demographic & Profile Data */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='demographic'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>4. Demographic & Profile Data</h2>
        <p className='text-gray-700'>
          We may collect non-identifiable, aggregated data (e.g., age range, city, product
          preferences) via optional surveys or analytics tools to improve our services. This data is
          used solely for internal business purposes and will not identify you individually without
          your consent.
        </p>
      </Card>

      {/* 5. Cookies */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='cookies'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>5. Cookies</h2>
        <p className='text-gray-700 mb-3'>
          We use cookies and similar technologies (e.g., pixels, local storage) to:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>Remember your preferences (e.g., language, cart items)</li>
          <li>Analyze site traffic and user behavior</li>
          <li>Enable targeted advertising (via trusted partners)</li>
        </ul>
        <p className='text-gray-700'>
          Our cookies do not store sensitive Personal Data (e.g., passwords, payment info). You can
          manage or disable cookies via your browser settings, though this may affect site
          functionality.
        </p>
      </Card>

      {/* 6. Data Retention */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='retention'>
        <div className='flex items-start mb-4'>
          <Clock className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>6. Data Retention</h2>
          </div>
        </div>
        <p className='text-gray-700 mb-3'>
          We retain your Personal Data only as long as necessary to:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>Fulfill the purposes outlined in this policy</li>
          <li>
            Comply with legal, tax, or regulatory obligations (e.g., 6 years for transaction records
            under Nigerian law)
          </li>
          <li>Resolve disputes or enforce agreements</li>
        </ul>
        <p className='text-gray-700'>
          Upon account deletion or after the retention period, we securely delete or anonymize your
          data.
        </p>
      </Card>

      {/* 7. Data Storage & Transfer */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='storage'>
        <div className='flex items-start mb-4'>
          <Globe className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>7. Data Storage & Transfer</h2>
          </div>
        </div>
        <p className='text-gray-700 mb-3'>
          Your Personal Data is primarily stored within Nigeria. If we transfer data outside
          Nigeria, we will:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>Ensure the recipient country has adequate data protection standards, or</li>
          <li>Obtain your explicit consent, or</li>
          <li>Implement safeguards (e.g., standard contractual clauses) as required by the NDPC</li>
        </ul>
        <p className='text-gray-700'>
          By using our services, you consent to such lawful transfers.
        </p>
      </Card>

      {/* 8. Your Rights */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-blue-50' id='rights'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>8. Your Rights as a Data Subject</h2>
        <p className='text-gray-700 mb-3'>
          Under the Nigeria Data Protection Act (NDPA) 2023, you have the right to:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>
            <strong>Access:</strong> Request a copy of your Personal Data in a structured,
            machine-readable format
          </li>
          <li>
            <strong>Rectification:</strong> Correct inaccurate or incomplete data
          </li>
          <li>
            <strong>Erasure ("Right to be Forgotten"):</strong> Request deletion where no legal
            basis for retention exists
          </li>
          <li>
            <strong>Restriction:</strong> Limit how we process your data in certain circumstances
          </li>
          <li>
            <strong>Portability:</strong> Transfer your data to another service provider
          </li>
          <li>
            <strong>Object:</strong> Withdraw consent or object to processing (e.g., marketing)
          </li>
          <li>
            <strong>Lodge a Complaint:</strong> Contact the Nigeria Data Protection Commission
            (NDPC) at https://ndpc.gov.ng or via email: info@ndpc.gov.ng
          </li>
        </ul>
        <p className='text-gray-700 mb-2'>
          To exercise any right, contact our Data Protection Officer (DPO) at:{" "}
          <strong>support@stedshop.com.ng</strong>
        </p>
        <p className='text-gray-700'>
          We will respond within 30 days (extendable by 15 days if complex).
        </p>
      </Card>

      {/* 9. Disclosure of Information */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='disclosure'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>9. Disclosure of Information</h2>
        <p className='text-gray-700 mb-3'>
          We may share your Personal Data only in the following circumstances:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>
            <strong>With Service Providers:</strong> Payment processors (e.g., Paystack,
            Flutterwave), delivery partners (e.g., GIG Logistics), and IT vendors — under strict
            data processing agreements
          </li>
          <li>
            <strong>For Legal Compliance:</strong> If required by law, court order, or to protect
            our rights, safety, or property
          </li>
          <li>
            <strong>In Business Transfers:</strong> In the event of a merger, acquisition, or asset
            sale, your data will be transferred under equivalent privacy obligations
          </li>
          <li>
            <strong>With Your Consent:</strong> For any other purpose disclosed at the time of
            collection
          </li>
        </ul>
        <p className='text-gray-700 font-semibold'>
          We do not share your data for third-party marketing without your opt-in.
        </p>
      </Card>

      {/* 10. Links to Other Websites */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='links'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>10. Links to Other Websites</h2>
        <p className='text-gray-700'>
          Our website may contain links to third-party sites (e.g., brand websites, social media).
          We are not responsible for their privacy practices. Please review their policies before
          submitting your information.
        </p>
      </Card>

      {/* 11. Security Measures */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='security'>
        <div className='flex items-start mb-4'>
          <Lock className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>11. Security Measures</h2>
          </div>
        </div>
        <p className='text-gray-700 mb-3'>
          We implement technical and organizational safeguards to protect your data, including:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>SSL/TLS encryption for data in transit</li>
          <li>Secure servers and access controls</li>
          <li>Regular security assessments</li>
          <li>Staff training on data protection</li>
        </ul>
        <p className='text-gray-700'>
          While no system is 100% secure, we take reasonable steps to prevent unauthorized access,
          loss, or misuse.
        </p>
      </Card>

      {/* 12. Data Breach Notification */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='breach'>
        <div className='flex items-start mb-4'>
          <Bell className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>12. Data Breach Notification</h2>
          </div>
        </div>
        <p className='text-gray-700 mb-3'>
          In the event of a personal data breach that poses a risk to your rights or freedoms:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700'>
          <li>
            We will notify the Nigeria Data Protection Commission (NDPC) within 72 hours of becoming
            aware of the breach
          </li>
          <li>
            We will notify you directly within 7 days if the breach is likely to result in high risk
            to your rights (e.g., identity theft)
          </li>
        </ul>
      </Card>

      {/* 13. Advertising */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='advertising'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>13. Advertising</h2>
        <p className='text-gray-700'>
          We may use third-party ad networks (e.g., Google Ads, Meta) to show relevant product ads.
          These partners may use non-identifiable data (e.g., cookies, device ID) to personalize
          ads. You can opt out via ad preference settings on their platforms.
        </p>
      </Card>

      {/* 14. Contact Us */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-gray-50' id='contact'>
        <div className='flex items-start mb-4'>
          <Mail className='h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0' />
          <div>
            <h2 className='text-xl font-bold text-gray-900'>14. Contact Us</h2>
          </div>
        </div>
        <p className='text-gray-700 mb-4'>
          For questions, requests, or complaints regarding this Privacy Policy or your Personal
          Data, please contact:
        </p>
        <div className='space-y-2 text-gray-700'>
          <p>
            <strong>Stedshop LTD</strong>
          </p>
          <p>
            <strong>Email:</strong> support@stedshop.com.ng
          </p>
          <p>
            <strong>Address:</strong> No 3 Chinyelugo Udozor street awada obosi, Anambra, Nigeria
          </p>
        </div>
      </Card>

      {/* Closing Statement */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-blue-50'>
        <p className='text-gray-700 text-center font-semibold'>
          Thank you for trusting Stedshop LTD with your personal information. We are committed to
          protecting your privacy and providing a safe, transparent shopping experience.
        </p>
      </Card>
    </div>
  );
};
